import dev.shanty.kotwire.stimulus.Controller
import dev.shanty.kotwire.stimulus.StimulusController
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.get

@JsExport
@StimulusController(
    targets = ["map", "itineraryPoint", "placePoint", "itineraryButton", "placesButton", "dayButton", "dayPicker", "cityPicker", "cityButton"],
    values = TripMapController.Values::class,
)
class TripMapController(context: dynamic) : Controller(context) {

    private val mapEl by mapTarget<HTMLElement>()
    private val zoom by zoomValue()
    private val initialMode by initialModeValue()
    private val initialLat by initialLatValue()
    private val initialLng by initialLngValue()
    private val itineraryPoints by itineraryPointTargets<HTMLElement>()
    private val placePoints by placePointTargets<HTMLElement>()
    private val itineraryButtons by itineraryButtonTargets<HTMLElement>()
    private val placesButtons by placesButtonTargets<HTMLElement>()
    private val dayButtons by dayButtonTargets<HTMLElement>()
    private val dayPickers by dayPickerTargets<HTMLElement>()
    private val cityPickers by cityPickerTargets<HTMLElement>()
    private val cityButtons by cityButtonTargets<HTMLElement>()

    private var lMap: LeafletMap? = null
    private var itineraryLayerGroup: LeafletLayerGroup? = null
    private var placesLayerGroup: LeafletLayerGroup? = null
    private var selectedDay: String = ""
    private var selectedCity: String = ""
    private var showingItinerary: Boolean = true

    object Values {
        val zoom: Int = 13
        val initialMode: String = ""
        val initialLat: String = ""
        val initialLng: String = ""
    }

    override fun connect() {
        val map = L.map(mapEl.id)
        lMap = map

        L.tileLayer(
            "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            LeafletTileLayerOptions(attribution = "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>")
        ).addTo(map)

        val paramLat = initialLat.toDoubleOrNull()
        val paramLng = initialLng.toDoubleOrNull()
        val paramMode = initialMode.takeIf { it.isNotEmpty() }

        val centerLat = paramLat ?: 51.508225918445184
        val centerLng = paramLng ?: -0.12805061748792648
        map.setView(L.latLng(centerLat, centerLng), zoom.toInt())

        val today = js("new Date().toISOString().split('T')[0]") as String
        val availableDays = itineraryPoints.mapNotNull { it.getAttribute("data-date") }.distinct().sorted()
        selectedDay = if (today in availableDays) today else availableDays.firstOrNull() ?: today

        dayButtons.forEach { btn ->
            val date = btn.getAttribute("data-date") ?: return@forEach
            btn.asDynamic().onclick = { onDaySelected(date) }
        }

        val availableCities = placePoints.mapNotNull { it.getAttribute("city") }.distinct().sorted()
        selectedCity = availableCities.firstOrNull() ?: ""

        itineraryLayerGroup = buildItineraryLayer()
        placesLayerGroup = buildPlacesLayer()

        if (paramMode == "places") {
            placesLayerGroup?.addTo(map)
            setActiveButton(activeIsItinerary = false)
            showingItinerary = false
            setPickerVisibility(showDayPicker = false)
            updateCityButtons()
            if (paramLat != null && paramLng != null) {
                map.setView(L.latLng(paramLat, paramLng), zoom.toInt())
            } else if (selectedCity.isNotEmpty()) {
                fitCityBounds(selectedCity)
            }
        } else {
            itineraryLayerGroup?.addTo(map)
            setActiveButton(activeIsItinerary = true)
            setPickerVisibility(showDayPicker = true)
            if (selectedDay.isNotEmpty()) fitDayBounds(selectedDay)
        }
        updateDayButtons()

        showCurrentLocation()
    }

    private fun buildItineraryLayer(): LeafletLayerGroup {
        val group = L.layerGroup()
        itineraryPoints.forEach { el ->
            if (el.getAttribute("data-date") != selectedDay) return@forEach
            val lat = el.getAttribute("latitude")?.toDoubleOrNull() ?: return@forEach
            val lng = el.getAttribute("longitude")?.toDoubleOrNull() ?: return@forEach
            val name = el.getAttribute("name") ?: "Place"
            val time = el.getAttribute("visittime") ?: ""
            val address = el.getAttribute("address") ?: ""
            val itemType = el.getAttribute("itemtype") ?: ""
            val placeType = el.getAttribute("type") ?: ""
            val href = el.getAttribute("href")
            val opts = js("{}")
            opts["icon"] = markerIcon(itemType, placeType)
            val marker = L.marker(L.latLng(lat, lng), opts)
            marker.bindPopup(buildPopup(name, address, time, lat, lng, href))
            group.addLayer(marker)
        }
        return group
    }

    private fun buildPlacesLayer(): LeafletLayerGroup {
        val group = L.layerGroup()
        placePoints.forEach { el ->
            val lat = el.getAttribute("latitude")?.toDoubleOrNull() ?: return@forEach
            val lng = el.getAttribute("longitude")?.toDoubleOrNull() ?: return@forEach
            val name = el.getAttribute("name") ?: "Place"
            val address = el.getAttribute("address") ?: ""
            val placeType = el.getAttribute("type") ?: ""
            val itemType = el.getAttribute("itemtype") ?: "PLACE_VISIT"
            val href = el.getAttribute("href")
            val opts = js("{}")
            opts["icon"] = markerIcon(itemType, placeType)
            val marker = L.marker(L.latLng(lat, lng), opts)
            marker.bindPopup(buildPopup(name, address, null, lat, lng, href))
            group.addLayer(marker)
        }
        return group
    }

    private fun markerIcon(itemType: String, placeType: String): dynamic {
        val (emoji, bg) = when (itemType) {
            "HOTEL"            -> "🏨" to "#6366F1"
            "FLIGHT_DEPARTURE" -> "✈️" to "#0EA5E9"
            "FLIGHT_ARRIVAL"   -> "🛬" to "#10B981"
            "BOOKING"          -> "🎫" to "#F59E0B"
            else -> when (placeType) {
                "BAKERY"      -> "🥐" to "#F97316"
                "COFFEE_SHOP" -> "☕" to "#92400E"
                "RESTAURANT"  -> "🍽️" to "#EF4444"
                "ATTRACTION"  -> "🎭" to "#7C3AED"
                else          -> "📍" to "#3B82F6"
            }
        }
        val html = """<div style="background:$bg;width:32px;height:32px;border-radius:50%;border:2px solid white;
            box-shadow:0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;
            justify-content:center;font-size:15px">$emoji</div>"""
        val opts = js("{}")
        opts["html"] = html
        opts["className"] = ""
        opts["iconSize"] = arrayOf(32, 32)
        opts["iconAnchor"] = arrayOf(16, 16)
        opts["popupAnchor"] = arrayOf(0, -18)
        return L.divIcon(opts)
    }

    private fun buildPopup(name: String, address: String, time: String?, lat: Double, lng: Double, detailUrl: String? = null): String {
        val mapsUrl = "https://www.google.com/maps/dir/?api=1&destination=$lat,$lng"
        val addressLine = if (address.isNotEmpty())
            """<p style="margin:2px 0 0;color:#666;font-size:0.8em">$address</p>""" else ""
        val timeLine = if (!time.isNullOrEmpty())
            """<p style="margin:2px 0 0;color:#888;font-size:0.8em">$time</p>""" else ""
        val navLink = """<a href="$mapsUrl" target="_blank" rel="noopener"
            style="display:inline-block;margin-top:6px;padding:3px 8px;background:#1a73e8;color:#fff;
                   border-radius:4px;font-size:0.8em;text-decoration:none">Navigate ↗</a>"""
        val detailLink = if (detailUrl != null)
            """<a href="$detailUrl"
                style="display:inline-block;margin-top:6px;margin-left:4px;padding:3px 8px;background:#6366f1;color:#fff;
                       border-radius:4px;font-size:0.8em;text-decoration:none">View details →</a>""" else ""
        return """<div style="line-height:1.4">
            <strong style="font-size:0.95em">$name</strong>
            $timeLine$addressLine
            <div>$navLink$detailLink</div>
            </div>"""
    }

    private fun showCurrentLocation() {
        val map = lMap ?: return
        val geo = window.navigator.asDynamic().geolocation ?: return
        geo.getCurrentPosition({ position: dynamic ->
            val lat = position.coords.latitude as Double
            val lng = position.coords.longitude as Double
            val marker = L.marker(L.latLng(lat, lng))
            marker.bindPopup("You are here")
            marker.addTo(map)
        })
    }

    @JsName("showItinerary")
    fun showItinerary() {
        val map = lMap ?: return
        placesLayerGroup?.removeFrom(map)
        itineraryLayerGroup?.addTo(map)
        setActiveButton(activeIsItinerary = true)
        setPickerVisibility(showDayPicker = true)
        showingItinerary = true
    }

    @JsName("showPlaces")
    fun showPlaces() {
        val map = lMap ?: return
        itineraryLayerGroup?.removeFrom(map)
        placesLayerGroup?.addTo(map)
        setActiveButton(activeIsItinerary = false)
        setPickerVisibility(showDayPicker = false)
        showingItinerary = false
        if (selectedCity.isNotEmpty()) fitCityBounds(selectedCity)
    }

    @JsName("selectCity")
    fun selectCity(event: dynamic) {
        val city = (event.currentTarget as? HTMLElement)?.getAttribute("data-city") ?: return
        onCitySelected(city)
    }

    private fun onCitySelected(city: String) {
        selectedCity = city
        updateCityButtons()
        fitCityBounds(city)
    }

    private fun fitCityBounds(city: String) {
        val map = lMap ?: return
        val cityPoints = placePoints.filter { it.getAttribute("city") == city }
        if (cityPoints.isEmpty()) return

        val featureGroup = L.featureGroup()
        cityPoints.forEach { el ->
            val lat = el.getAttribute("latitude")?.toDoubleOrNull() ?: return@forEach
            val lng = el.getAttribute("longitude")?.toDoubleOrNull() ?: return@forEach
            featureGroup.addLayer(L.marker(L.latLng(lat, lng)))
        }

        val bounds = featureGroup.getBounds()
        val opts = js("{}")
        opts["padding"] = arrayOf(60, 60)
        map.fitBounds(bounds, opts)
    }

    private fun setPickerVisibility(showDayPicker: Boolean) {
        dayPickers.forEach { el ->
            if (showDayPicker) el.classList.remove("hidden") else el.classList.add("hidden")
        }
        cityPickers.forEach { el ->
            if (!showDayPicker) el.classList.remove("hidden") else el.classList.add("hidden")
        }
    }

    private fun updateCityButtons() {
        cityButtons.forEach { btn ->
            val city = btn.getAttribute("data-city") ?: return@forEach
            if (city == selectedCity) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-accent-secondary", "text-white")
            } else {
                btn.classList.remove("bg-accent-secondary", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
    }

    private fun onDaySelected(date: String) {
        selectedDay = date
        val map = lMap ?: return
        itineraryLayerGroup?.removeFrom(map)
        itineraryLayerGroup = buildItineraryLayer()
        if (showingItinerary) itineraryLayerGroup?.addTo(map)
        updateDayButtons()
        fitDayBounds(date)
    }

    private fun fitDayBounds(date: String) {
        val map = lMap ?: return
        val dayPoints = itineraryPoints.filter { it.getAttribute("data-date") == date }
        if (dayPoints.isEmpty()) return

        val featureGroup = L.featureGroup()
        dayPoints.forEach { el ->
            val lat = el.getAttribute("latitude")?.toDoubleOrNull() ?: return@forEach
            val lng = el.getAttribute("longitude")?.toDoubleOrNull() ?: return@forEach
            featureGroup.addLayer(L.marker(L.latLng(lat, lng)))
        }

        val bounds = featureGroup.getBounds()
        val opts = js("{}")
        opts["padding"] = arrayOf(60, 60)
        map.fitBounds(bounds, opts)
    }

    private fun updateDayButtons() {
        dayButtons.forEach { btn ->
            val date = btn.getAttribute("data-date") ?: return@forEach
            if (date == selectedDay) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-accent-secondary", "text-white")
            } else {
                btn.classList.remove("bg-accent-secondary", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
    }

    private fun setActiveButton(activeIsItinerary: Boolean) {
        itineraryButtons.forEach { btn ->
            if (activeIsItinerary) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-accent-secondary", "text-white")
            } else {
                btn.classList.remove("bg-accent-secondary", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
        placesButtons.forEach { btn ->
            if (!activeIsItinerary) {
                btn.classList.remove("bg-gray-100", "text-gray-600")
                btn.classList.add("bg-accent-secondary", "text-white")
            } else {
                btn.classList.remove("bg-accent-secondary", "text-white")
                btn.classList.add("bg-gray-100", "text-gray-600")
            }
        }
    }
}
